import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  GetParams,
  GetResponse,
  CompanyTaxes,
  CompanyTaxesCreate,
  CompanyTaxesType,
  PostResponse,
  User,
  UserCreate,
} from '@app/shared';
import { convertGetParamsToHttpParams, CompanyService } from '@app/core';

/**
 * Parámetros para pedir el catálogo de impuestos ENTERO, igual que la carga de GlobalValuesService.
 *
 * `companytaxes` ignora la compañía de la cabecera y devuelve los impuestos de todas, así que quien
 * filtre por compañía en el cliente necesita el catálogo completo: con la página de 100 de los
 * maestros, en PRE (105 filas, 14/09/2026) se perdían los últimos impuestos de CEC 5 y CEC 6.
 */
export const COMPANY_TAX_CATALOG_PARAMS: GetParams = {
  startRow: 0,
  endRow: 5000,
  sortDirection: 'asc',
  sortProperty: 'id',
};

@Injectable({
  providedIn: 'root',
})
export class CompanyTaxesService {
  /** URLs principales relacionadas con CompanyTaxess **/
  CompanyTaxesUrl = environment.coreApiBaseUrl + '/api/companytaxes';
  CompanyTaxesTypeUrl = environment.coreApiBaseUrl + '/api/companytaxes-type';
  userUrl = environment.identityApiBaseUrl + '/api/user';

  constructor(
    private http: HttpClient,
    private companyService: CompanyService
  ) {}

  /** Metodos de obtener/crear/actualizar/eliminar CompanyTaxess **/
  getCompanyTaxes(
    getParams: GetParams,
    accessToken?: string
  ): Observable<GetResponse> {
    const companyId = this.companyService.selectedCompanyId$.getValue();
    let headers = new HttpHeaders({
      'accept': 'application/json',
      'Content-Type': 'application/json',
    });
    
    if (companyId) {
      headers = headers.set('companyId', companyId.toString());
    }

    // Si el accessToken es proporcionado, agregamos el header Authorization
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Realizamos la petición GET con los parámetros y, si es necesario, con el token de autorización
    return this.http.get<GetResponse>(this.CompanyTaxesUrl, {
      params: convertGetParamsToHttpParams(getParams),
      headers: headers, // Pasamos los headers
    });
  }

  createCompanyTaxes(
    newCompanyTaxes: CompanyTaxesCreate
  ): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.CompanyTaxesUrl, newCompanyTaxes);
  }

  updateCompanyTaxes(CompanyTaxes: CompanyTaxes): Observable<any> {
    return this.http.put(this.CompanyTaxesUrl, CompanyTaxes);
  }

  deleteCompanyTaxes(CompanyTaxessIds: number[]): Observable<any> {
    return this.http.delete(this.CompanyTaxesUrl, {
      body: { ids: CompanyTaxessIds },
    });
  }

  /** Metodos de obtener/crear/actualizar/eliminar CompanyTaxesTypes **/
  getCompanyTaxesType(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.CompanyTaxesTypeUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createCompanyTaxesType(newCompanyTaxesType: {
    name: string;
    isActive: boolean;
  }): Observable<PostResponse> {
    return this.http.post<PostResponse>(
      this.CompanyTaxesTypeUrl,
      newCompanyTaxesType
    );
  }

  updateCompanyTaxesType(CompanyTaxesType: CompanyTaxesType): Observable<any> {
    return this.http.put(this.CompanyTaxesTypeUrl, CompanyTaxesType);
  }

  deleteCompanyTaxesType(CompanyTaxesTypesIds: number[]): Observable<any> {
    return this.http.delete(this.CompanyTaxesTypeUrl, {
      body: { ids: CompanyTaxesTypesIds },
    });
  }

  /** Metodos de obtener/crear/actualizar/eliminar User **/
  getUser(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.userUrl, {
      params: convertGetParamsToHttpParams(getParams),
    });
  }

  createUser(newUser: UserCreate): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.userUrl, newUser);
  }

  updateUser(User: User): Observable<any> {
    return this.http.put(this.userUrl, User);
  }

  deleteUser(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.userUrl, {
      body: { ids: desconsolidationsTypeIds },
    });
  }
}
